import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { clearTokens } from '../utils/token';
import { getGlobalNotify } from '../components/ErrorNotification';

let isRefreshing = false;
let refreshSubscribers: Array<() => void> = [];

function onTokenRefreshed() {
  refreshSubscribers.forEach((cb) => cb());
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: () => void) {
  refreshSubscribers.push(cb);
}

function generateRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function createApiClient(baseURL: string = '/api'): AxiosInstance {
  const client = axios.create({
    baseURL,
    withCredentials: true,
    timeout: 30_000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      config.headers['X-Request-Id'] = generateRequestId();
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor: auto-refresh on 401
  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const requestUrl = String(originalRequest?.url || '');

      if (error.response?.status === 401
        && originalRequest
        && !originalRequest._retry
        && !requestUrl.includes('/auth/api/v1/auth/refresh')
        && !requestUrl.includes('/api/v1/auth/refresh')) {

        if (isRefreshing) {
          return new Promise((resolve) => {
            addRefreshSubscriber(() => {
              resolve(client(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          await axios.post('/auth/api/v1/auth/refresh', {}, { withCredentials: true });
          isRefreshing = false;
          onTokenRefreshed();
          return client(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          clearTokens();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      // Auto-notify on server errors (5xx) that aren't handled by the caller
      if (error.response?.status >= 500) {
        const msg = error.response?.data?.message || '服务器错误，请稍后重试';
        getGlobalNotify()(msg, 'error');
      }

      return Promise.reject(error);
    }
  );

  return client;
}

export const apiClient = createApiClient();
